import { useCallback } from 'react';
import { useRequiredParamsSetter } from '../../../../context/TargetingSettingsContext';
import type {
  Element,
  RequiredParamsType,
  RequiredTargetingSectionKeysType,
  Section,
  TargetingSettingsContextType,
} from '../../../../context/types';
import { restoreDefaultOnClickToSection } from '../utils/restoreDefaultOnClickToSection';

export type GetOneTimeHandlersArgs = {
  prev: RequiredParamsType;
  sectionKey: RequiredTargetingSectionKeysType;
  oneTimeAction: (
    set: TargetingSettingsContextType['setState'],
    sectionKey: RequiredTargetingSectionKeysType,
    elementIndex: number,
    subIndex?: number
  ) => void;
  initialState: Section;
};

export const useOneTimeHandlers = () => {
  const set = useRequiredParamsSetter();

  const getOneTimeHandlers = useCallback(
    ({ prev, sectionKey, oneTimeAction, initialState }: GetOneTimeHandlersArgs) => {
      const elements = prev.sections[sectionKey].elements;
      const originalSubOnClick = elements.map(element =>
        element.subOptions ? element.subOptions.map(sub => sub.onClick) : []
      );
      const originalMainOnClicks = elements.map(element => element.mainOption.onClick);

      const createOneTimeMainClick = (
        elementIndex: number,
        originalMainOnClick:
          | ((event, sectionKey: RequiredTargetingSectionKeysType, elementIndex: number) => void)
          | undefined
      ) => {
        return (event, sectionKey: RequiredTargetingSectionKeysType, eventElementIndex: number) => {
          oneTimeAction(set, sectionKey, elementIndex, eventElementIndex);
          originalMainOnClick?.(event, sectionKey, elementIndex);
          restoreDefaultOnClickToSection({ sectionKey, defaultState: initialState, set });
        };
      };

      return elements.map((element: Element, elementIndex: number) => {
        const oneTimeMainClick = createOneTimeMainClick(elementIndex, originalMainOnClicks[elementIndex]);

        const oneTimeSubOptionsClick = element.subOptions?.map((subOption, subOptionIndex: number) => ({
          ...subOption,
          onClick: (
            event,
            sectionKey: RequiredTargetingSectionKeysType,
            eventElementIndex: number,
            eventSubOptionIndex: number | undefined
          ) => {
            oneTimeAction(set, sectionKey, eventElementIndex, eventSubOptionIndex);

            if (eventSubOptionIndex !== undefined)
              originalSubOnClick[elementIndex][subOptionIndex]?.(
                event,
                sectionKey,
                eventElementIndex,
                eventSubOptionIndex
              );

            restoreDefaultOnClickToSection({ sectionKey, defaultState: initialState, set });
          },
        }));

        return {
          ...element,
          mainOption: {
            ...element.mainOption,
            onClick: oneTimeMainClick,
          },
          subOptions: oneTimeSubOptionsClick,
        };
      });
    },
    [set]
  );

  return {
    getOneTimeHandlers,
  };
};
